package com.klantenstop.web.data.models.dto.responses.moderation

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Full moderation entry detail including the resolved snapshot data.
 * Exactly one of [companySnapshot], [statusSnapshot], [reviewSnapshot] will be non-null,
 * depending on [ModerationEntryDto.entityType].
 */
@Serializable
data class ModerationEntryDetailDto(
    val entry: ModerationEntryDto,
    @SerialName("company_snapshot")
    val companySnapshot: CompanySnapshotDto? = null,
    @SerialName("status_snapshot")
    val statusSnapshot: StatusSnapshotDto? = null,
    @SerialName("review_snapshot")
    val reviewSnapshot: ReviewSnapshotDto? = null
)

/** Lightweight category reference — id + resolved name. */
@Serializable
data class CategoryNameDto(
    val id: String = "",
    val name: String = "",
)

/** Snapshot data for a company submission. Address fields are stored inline (denormalised). */
@Serializable
data class CompanySnapshotDto(
    val id: String = "",
    @SerialName("company_id")
    val companyId: String = "",
    val name: String = "",
    val description: String = "",
    @SerialName("street_name")
    val streetName: String = "",
    @SerialName("street_number")
    val streetNumber: String = "",
    @SerialName("zip_code")
    val zipCode: String = "",
    val city: String = "",
    val country: String = "",
    val longitude: Double = 0.0,
    val latitude: Double = 0.0,
    @SerialName("category_ids")
    val categoryIds: List<String> = emptyList(),
    /** Resolved category objects — populated by the backend. */
    val categories: List<CategoryNameDto> = emptyList(),
    @SerialName("phone_number")
    val phoneNumber: String = "",
    @SerialName("email_address")
    val emailAddress: String = "",
    val website: String = "",
    @SerialName("created_at")
    val createdAt: String = ""
) {
    val fullAddress get() = "$streetName $streetNumber, $zipCode $city, $country"

}

@Serializable
data class StatusSnapshotDto(
    val id: String = "",
    @SerialName("status_id")
    val statusId: String = "",
    @SerialName("company_id")
    val companyId: String = "",
    /** Resolved company name — populated by the backend. */
    @SerialName("company_name")
    val companyName: String? = null,
    val text: String = "",
    @SerialName("status_type")
    val statusType: String = "",
    @SerialName("created_at")
    val createdAt: String = ""
)

@Serializable
data class ReviewSnapshotDto(
    val id: String = "",
    @SerialName("review_id")
    val reviewId: String = "",
    @SerialName("company_id")
    val companyId: String = "",
    /** Resolved company name — populated by the backend. */
    @SerialName("company_name")
    val companyName: String? = null,
    val text: String = "",
    val rating: Int = 0,
    val anonymous: Boolean = false,
    /** Resolved reviewer name — populated by the backend. */
    @SerialName("submitted_by_user_name")
    val submittedByUserName: String? = null,
    @SerialName("created_at")
    val createdAt: String = ""
)

@Serializable
data class RejectDecisionDto(
    @SerialName("rejection_reason")
    val rejectionReason: String? = null
)
